import { For, Match, Show, Switch, type JSX } from "solid-js";

import styles from "./art.module.css";
import { useArtRef, type ArtDefId } from "./art_defs";
import { CANALS, CAP, CROWN, PULP, STUMP, TOOTH, rootLeftX, rootRightX } from "./tooth_paths";

type ToothOverlay =
  | "crack_small"
  | "crack_long"
  | "amalgam"
  | "amalgam_crack"
  | "decay_small"
  | "decay_deep"
  | "decay_nerve"
  | "core"
  | "canals_filled"
  | "small_filling"
  | "broken_cusp"
  | "big_break"
  | "abscess"
  | "abscess_spread"
  | "crown_cap"
  | "tartar"
  | "tartar_heavy"
  | "scaler"
  | "laser"
  | "probe"
  | "check";

/**
 * A molar from the side, as in a patient-education cross-section. `gum` and `bone` are the y of the gum line and
 * the bone crest (larger is lower, so less support); `stump` is the tooth shaped for a crown.
 */
export type ToothSpec = {
  overlays?: ToothOverlay[];
  gum?: number;
  bone?: number;
  tilt?: number;
  dashed?: boolean;
  pulp?: "ok" | "hot" | "dead";
  hotGums?: boolean;
  pocket?: boolean;
  shadow?: boolean;
  stump?: boolean;
};

export function ToothArt(props: { spec: ToothSpec; title: string; viewBox?: string }): JSX.Element {
  return (
    <svg
      viewBox={props.viewBox ?? "0 0 120 176"}
      class={styles["tooth-art"]}
      role="img"
      aria-label={props.title}
    >
      <title>{props.title}</title>
      <ToothBody spec={props.spec} />
    </svg>
  );
}

/** The drawing without its svg, for the treatment pictures that add to it. */
export function ToothBody(props: { spec: ToothSpec }): JSX.Element {
  const overlays = () => props.spec.overlays ?? [];
  return (
    <>
      <Show when={props.spec.shadow}>
        <ellipse
          cx="60"
          cy="174"
          rx="38"
          ry="6"
          fill="#0D2430"
          opacity=".16"
          filter={useArtRef("f-blur3")}
        />
      </Show>
      <Show when={props.spec.gum}>
        {(gum) => (
          <Tissue
            gum={gum()}
            bone={props.spec.bone}
            hot={props.spec.hotGums}
            pocket={props.spec.pocket}
          />
        )}
      </Show>
      <g transform={props.spec.tilt ? `rotate(${props.spec.tilt} 60 120)` : undefined}>
        <Show
          when={!props.spec.dashed}
          fallback={
            <path
              d={TOOTH}
              fill="#FFFFFF"
              fill-opacity=".35"
              stroke="#9AA7AF"
              stroke-width="1.8"
              stroke-dasharray="5 5"
            />
          }
        >
          <SolidTooth spec={props.spec} />
        </Show>
        <For each={overlays()}>{(overlay) => EXTERNAL[overlay]?.()}</For>
      </g>
    </>
  );
}

const PULP_FILL = {
  ok: "g-pulp",
  hot: "g-pulp-hot",
  dead: "g-pulp-dead",
} as const satisfies Record<NonNullable<ToothSpec["pulp"]>, ArtDefId>;

function SolidTooth(props: { spec: ToothSpec }): JSX.Element {
  const overlays = () => props.spec.overlays ?? [];
  // A break hides part of the tooth through a mask and draws the broken edge on top.
  const broken = () =>
    overlays().includes("big_break") ? "big" : overlays().includes("broken_cusp") ? "cusp" : null;
  return (
    <>
      <g
        mask={
          broken() === "big"
            ? useArtRef("m-break")
            : broken() === "cusp"
              ? useArtRef("m-cusp")
              : undefined
        }
      >
        <Show
          when={props.spec.stump}
          fallback={
            <>
              <path d={TOOTH} fill={useArtRef("g-root")} />
              <path d={CROWN} fill={useArtRef("g-enamel")} />
              <Show when={props.spec.pulp === "hot"}>
                <path d={PULP} fill="#FF4A36" opacity=".55" filter={useArtRef("f-blur3")} />
              </Show>
              <Show when={props.spec.pulp}>
                {(pulp) => <path d={PULP} fill={useArtRef(PULP_FILL[pulp()])} />}
              </Show>
              <path d={TOOTH} fill="none" stroke="#8E9CA5" stroke-width="1.7" />
              <path d="M23,78 C40,84 80,84 97,78" fill="none" stroke="#D6C9B3" stroke-width="1.2" />
              <path
                d="M31,52 C32,39 38,29 47,26"
                fill="none"
                stroke="#FFFFFF"
                stroke-width="4"
                stroke-linecap="round"
                opacity=".95"
              />
            </>
          }
        >
          <g clip-path={useArtRef("c-root")}>
            <path d={TOOTH} fill={useArtRef("g-root")} stroke="#8E9CA5" stroke-width="1.7" />
          </g>
          <path d={STUMP} fill={useArtRef("g-dentin")} stroke="#8E9CA5" stroke-width="1.7" />
          <Show when={props.spec.pulp}>
            <path
              d="M52,94 C51,118 46,140 43,152 M68,94 C69,118 74,140 77,152"
              fill="none"
              stroke={useArtRef("g-pulp")}
              stroke-width="3.4"
              stroke-linecap="round"
            />
          </Show>
        </Show>
        <For each={overlays()}>{(overlay) => INTERNAL[overlay]?.()}</For>
      </g>
      <Switch>
        <Match when={broken() === "big"}>
          <path
            d="M98,78 L66,92 L70,66 L58,52 L60,30"
            fill="none"
            stroke="#8E9CA5"
            stroke-width="1.8"
            stroke-linejoin="round"
          />
        </Match>
        <Match when={broken() === "cusp"}>
          <path
            d="M98,78 L84,70 L88,52 L78,44 L74,30"
            fill="none"
            stroke="#8E9CA5"
            stroke-width="1.8"
            stroke-linejoin="round"
          />
        </Match>
      </Switch>
    </>
  );
}

// The tissue's top edge, as offsets from its y: where it starts at the edge, its two control points, and the
// level at a tooth's neck. The gum line and the bone crest have their own shapes. The gap scene draws its own lines
// across the gap from the same numbers.
type Ridge = readonly [number, number, number, number];
export const GUM_RIDGE: Ridge = [-2, -7, -3, 4];
export const BONE_RIDGE: Ridge = [0, -4, 0, 6];
/** How far a tooth's neck reaches either side of its center: the tissue runs flat under the tooth between the two. */
export const NECK_SPAN = 26;

/** Every tooth center the tissue dips to, left to right: one for a lone tooth, more for a row. */
type Centers = readonly [number, ...number[]];

function ridge(y: number, [o0, o1, o2, o3]: Ridge, centers: Centers, x0: number, x1: number) {
  const first = centers[0];
  const last = centers.at(-1) ?? first;
  const neighbors = centers.slice(1).map((next, i) => [centers[i] ?? first, next] as const);
  // From one tooth's neck up over the papilla and down to the next tooth's neck.
  const papilla = ([prev, next]: readonly [number, number]) => {
    const mid = (prev + next) / 2;
    return (
      `C${prev + 38},${y + o2} ${mid - 10},${y + o1} ${mid},${y + o1} ` +
      `C${mid + 10},${y + o1} ${next - 38},${y + o2} ${next - NECK_SPAN},${y + o3}`
    );
  };
  const left = `M${x0},${y + o0} C${first - 54},${y + o1} ${first - 38},${y + o2} ${first - NECK_SPAN},${y + o3}`;
  return {
    /** The whole edge, left to right, flat under each tooth. */
    edge: [
      left,
      ...neighbors.map((pair) => `L${pair[0] + NECK_SPAN},${y + o3} ${papilla(pair)}`),
      `L${last + NECK_SPAN},${y + o3} C${last + 38},${y + o2} ${last + 54},${y + o1} ${x1},${y + o0}`,
    ].join(" "),
    /** Only the stretches that show beside the teeth: outside the row and between neighbors. */
    visible: [
      left,
      ...neighbors.map((pair) => `M${pair[0] + NECK_SPAN},${y + o3} ${papilla(pair)}`),
      `M${x1},${y + o0} C${last + 54},${y + o1} ${last + 38},${y + o2} ${last + NECK_SPAN},${y + o3}`,
    ].join(" "),
  };
}

/**
 * The gum and bone in cross-section behind the teeth, faded at its edges. `centers` are the teeth it hugs (a
 * lone tooth at 60 by default); `pocket` draws a lone tooth's gum pockets down to the bone.
 */
export function Tissue(props: {
  gum: number;
  bone?: number;
  hot?: boolean;
  pocket?: boolean;
  centers?: Centers;
  x0?: number;
  x1?: number;
}): JSX.Element {
  const centers = () => props.centers ?? [60];
  const x0 = () => props.x0 ?? -14;
  const x1 = () => props.x1 ?? 134;
  const gum = () => ridge(props.gum, GUM_RIDGE, centers(), x0(), x1());
  const floor = () => (props.bone ?? props.gum + 32) + 10;
  return (
    <g mask={useArtRef("m-vignette")}>
      <path
        d={`${gum().edge} L${x1()},${floor()} L${x0()},${floor()} Z`}
        fill={props.hot ? useArtRef("g-gum-hot") : useArtRef("g-gum")}
      />
      <Show when={props.bone}>
        {(bone) => {
          const crest = () => ridge(bone(), BONE_RIDGE, centers(), x0(), x1());
          return (
            <>
              <path d={`${crest().edge} L${x1()},200 L${x0()},200 Z`} fill={useArtRef("p-bone")} />
              <path d={crest().visible} fill="none" stroke="#D8C49F" stroke-width="1.4" />
            </>
          );
        }}
      </Show>
      <path
        d={gum().visible}
        fill="none"
        stroke="#FCE3E0"
        stroke-width="1.8"
        stroke-linecap="round"
      />
      <Show when={props.pocket && props.bone}>
        {(bone) => <path d={pocketPath(props.gum, bone())} fill="#7E2F35" opacity=".55" />}
      </Show>
    </g>
  );
}

/** The two wedges between the gum and the root surface, down to just above the bone. */
function pocketPath(gum: number, bone: number): string {
  const top = gum + 3;
  const bottom = bone - 4;
  const f = (v: number) => v.toFixed(1);
  const [l0, l1, r0, r1] = [rootLeftX(top), rootLeftX(bottom), rootRightX(top), rootRightX(bottom)];
  return (
    `M${f(l0 - 5)},${top} L${f(l0 + 2)},${top} L${f(l1 + 2)},${bottom} L${f(l1 - 1)},${bottom} Z ` +
    `M${f(r0 + 5)},${top} L${f(r0 - 2)},${top} L${f(r1 - 2)},${bottom} L${f(r1 + 1)},${bottom} Z`
  );
}

function Crack(props: { d: string }): JSX.Element {
  return (
    <>
      <path
        d={props.d}
        fill="none"
        stroke="#FFFFFF"
        stroke-opacity=".75"
        stroke-width="5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d={props.d}
        fill="none"
        stroke="#25292D"
        stroke-width="2.2"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </>
  );
}

function Amalgam(): JSX.Element {
  return (
    <>
      <path
        d="M38,30 C46,38 74,38 82,30 L80,50 C70,56 50,56 40,50 Z"
        fill={useArtRef("g-amalgam")}
        stroke="#5F6970"
        stroke-width="1.3"
      />
      <path
        d="M44,40 C52,44 62,44 70,41"
        fill="none"
        stroke="#FFFFFF"
        stroke-opacity=".55"
        stroke-width="2.2"
        stroke-linecap="round"
      />
    </>
  );
}

/** A crown's porcelain cap, seated: the tooth outline's own shape, slightly larger. */
export function CrownCap(): JSX.Element {
  return (
    <>
      <path
        d={CAP}
        fill={useArtRef("g-porcelain")}
        class={styles["brand-stroke"]}
        stroke-width="2.6"
      />
      <path
        d="M30,52 C31,38 37,27 46,24"
        fill="none"
        stroke="#FFFFFF"
        stroke-width="4"
        stroke-linecap="round"
      />
      <path
        d="M30,74 C50,79 70,79 90,74"
        fill="none"
        class={styles["brand-soft-stroke"]}
        stroke-width="4"
        stroke-linecap="round"
      />
    </>
  );
}

const SMALL_FILLING = "M64,28 C70,33 80,32 85,28 L83,42 C77,47 70,47 66,42 Z";

// Drawn inside the tooth, under its break mask.
const INTERNAL: Partial<Record<ToothOverlay, () => JSX.Element>> = {
  crack_small: () => <Crack d="M58,24 L62,40 L57,54" />,
  crack_long: () => <Crack d="M58,24 L62,42 L56,60 L61,82 L57,104" />,
  amalgam: Amalgam,
  amalgam_crack: () => (
    <>
      <Amalgam />
      <Crack d="M82,32 L90,46 L86,60" />
    </>
  ),
  decay_small: () => (
    <path
      d="M74,33 C80,31 85,36 83,42 C81,47 74,48 71,43 C69,39 70,35 74,33 Z"
      fill={useArtRef("g-decay")}
    />
  ),
  decay_deep: () => (
    <path
      d="M68,30 C80,27 89,36 86,50 C84,60 76,66 68,62 C62,57 60,46 62,38 C63,34 65,31 68,30 Z"
      fill={useArtRef("g-decay")}
    />
  ),
  decay_nerve: () => (
    <path
      d="M68,28 C82,25 91,38 87,54 C84,64 76,71 67,67 C61,62 59,48 61,38 C62,32 64,29 68,28 Z"
      fill={useArtRef("g-decay")}
    />
  ),
  core: () => (
    <path
      d="M36,36 C44,44 76,44 84,36 L84,70 C70,76 50,76 36,70 Z"
      fill={useArtRef("g-core")}
      stroke="#6E93AD"
      stroke-width="1.3"
    />
  ),
  canals_filled: () => (
    <>
      <path d="M50,70 C52,60 68,60 70,70 L68,92 L52,92 Z" fill={useArtRef("g-gutta")} />
      <path
        d={CANALS}
        fill="none"
        stroke={useArtRef("g-gutta")}
        stroke-width="4.4"
        stroke-linecap="round"
      />
      <path
        d="M48,30 C54,34 66,34 72,30 L70,62 C64,66 56,66 50,62 Z"
        fill={useArtRef("g-composite")}
        stroke="#CDBE9F"
        stroke-width="1.2"
      />
    </>
  ),
  small_filling: () => (
    <>
      <path d={SMALL_FILLING} fill={useArtRef("g-composite")} />
      <path
        d={SMALL_FILLING}
        class={styles["brand-stroke"]}
        fill="none"
        stroke-width="1.6"
        stroke-dasharray="3 2.4"
      />
    </>
  ),
};

// Drawn over the tooth and outside its mask: what sits on, around or beside it.
const EXTERNAL: Partial<Record<ToothOverlay, () => JSX.Element>> = {
  abscess: () => (
    <>
      <circle cx="43" cy="160" r="19" fill={useArtRef("g-hot-glow")} />
      <circle cx="43" cy="160" r="6.5" fill="#C9301F" opacity=".85" />
    </>
  ),
  // Past the root tip: the infection reaches the bone around both roots.
  abscess_spread: () => (
    <>
      <circle cx="46" cy="158" r="30" fill={useArtRef("g-hot-glow")} />
      <circle cx="76" cy="158" r="26" fill={useArtRef("g-hot-glow")} />
      <circle cx="43" cy="160" r="9" fill="#C9301F" opacity=".85" />
      <circle cx="77" cy="160" r="7" fill="#C9301F" opacity=".8" />
    </>
  ),
  crown_cap: CrownCap,
  tartar: () => (
    <g fill={useArtRef("g-tartar")} stroke="#9C7838" stroke-width=".8">
      <path d="M22,88 C27,86 29,92 26,96 C23,99 19,96 20,92 Z" />
      <path d="M24,99 C28,98 29,104 26,106 C23,108 21,104 22,101 Z" />
      <path d="M98,88 C93,86 91,92 94,96 C97,99 101,96 100,92 Z" />
      <path d="M96,99 C92,98 91,104 94,106 C97,108 99,104 98,101 Z" />
    </g>
  ),
  tartar_heavy: () => (
    <g fill={useArtRef("g-tartar")} stroke="#9C7838" stroke-width=".8">
      <path d="M21,87 C27,84 30,92 27,97 C24,101 18,97 19,92 Z" />
      <path d="M23,99 C29,97 30,105 27,108 C24,111 20,106 21,102 Z" />
      <path d="M26,111 C30,110 31,116 28,118 C25,120 23,116 24,113 Z" />
      <path d="M99,87 C93,84 90,92 93,97 C96,101 102,97 101,92 Z" />
      <path d="M97,99 C91,97 90,105 93,108 C96,111 100,106 99,102 Z" />
      <path d="M94,111 C90,110 89,116 92,118 C95,120 97,116 96,113 Z" />
    </g>
  ),
  scaler: () => (
    <>
      <path
        d="M131,-14 L114,36"
        stroke={useArtRef("g-steel")}
        stroke-width="9"
        stroke-linecap="round"
      />
      <path
        d="M126,-2 L123,6 M123,6 L120,14 M120,14 L117,22"
        stroke="#5E6A72"
        stroke-width="9"
        stroke-opacity=".25"
      />
      <path
        d="M114,36 C110,60 103,82 100,96 C99,102 95,104 93,100"
        fill="none"
        stroke="#6B7780"
        stroke-width="2.6"
        stroke-linecap="round"
      />
      <path
        d="M107,92 C110,96 110,101 107,105 M111,89 C115,95 115,102 111,108"
        fill="none"
        class={styles["brand-stroke"]}
        stroke-width="1.8"
        stroke-linecap="round"
      />
    </>
  ),
  laser: () => (
    <>
      <path
        d="M131,-14 L112,40"
        stroke={useArtRef("g-steel")}
        stroke-width="10"
        stroke-linecap="round"
      />
      <path
        d="M112,40 L99,96"
        stroke="#8B5CF6"
        stroke-width="9"
        stroke-linecap="round"
        opacity=".35"
        filter={useArtRef("f-blur3")}
      />
      <path d="M112,40 L99,96" stroke="#D9DEE2" stroke-width="2.2" stroke-linecap="round" />
      <circle cx="99" cy="97" r="8" fill="#8B5CF6" opacity=".55" filter={useArtRef("f-blur3")} />
      <circle cx="99" cy="97" r="2.6" fill="#EDE9FE" />
    </>
  ),
  probe: () => (
    <>
      <path
        d="M130,-14 L113,40"
        stroke={useArtRef("g-steel")}
        stroke-width="7"
        stroke-linecap="round"
      />
      <path d="M113,40 L101,90" stroke="#7C878F" stroke-width="2.4" stroke-linecap="round" />
      <path d="M104.4,76 L102.6,83.5" stroke="#26313A" stroke-width="3.6" />
    </>
  ),
  check: () => (
    <>
      <circle cx="21" cy="20" r="14" fill="#E3F4EA" stroke="#2E8B57" stroke-width="1.6" />
      <path
        d="M14.5,20 L19.5,25 L28,15.5"
        fill="none"
        stroke="#2E8B57"
        stroke-width="3"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </>
  ),
};
